import { emitTelemetryEvent } from 'utils/sentry/telemetryEvent';
import { isDomException } from 'utils/types';

export const GUM_PENDING_THRESHOLD_MS = 10_000;

export type PendingUserMediaFlow = 'fast-join' | 'join-screen';
export type PendingUserMediaAction = 'reload' | 'join-without-media';

type MediaPermissionName = 'camera' | 'microphone';

export interface WatchPendingUserMediaOptions {
  flow: PendingUserMediaFlow;
  labelsAtStartup: boolean;
  onStalled: () => void;
  onRecovered: () => void;
}

export interface PendingUserMediaWatchdog<T> {
  settled: Promise<T | undefined>;
  abandon: (action: PendingUserMediaAction) => void;
}

const CHANNEL = 'user_media';

// Firefox rejects these names and older WebKit lacks navigator.permissions; both become null.
const queryPermission = async (name: MediaPermissionName): Promise<PermissionStatus | null> => {
  try {
    return await navigator.permissions.query({ name });
  } catch {
    return null;
  }
};

const userInteracted = (): boolean | undefined =>
  (navigator as { userActivation?: { hasBeenActive: boolean } }).userActivation?.hasBeenActive;

const rejectionOutcome = (error: unknown) =>
  `rejected:${isDomException(error) ? error.name : 'unknown'}`;

export const watchPendingUserMedia = <T>(
  request: Promise<T>,
  { flow, labelsAtStartup, onStalled, onRecovered }: WatchPendingUserMediaOptions
): PendingUserMediaWatchdog<T> => {
  const startedAt = performance.now();
  const elapsedMs = () => Math.round(performance.now() - startedAt);

  let pending = true;
  let stalled = false;
  let abandoned = false;
  const unsubscribers: Array<() => void> = [];
  const grantedAt = new Map<MediaPermissionName, number>();

  const emitGrantedWhilePending = (name: MediaPermissionName, elapsed: number) =>
    emitTelemetryEvent({
      event: 'media.gum.pending.granted',
      message: `[GUM] permission granted while pending — ${name}`,
      level: 'warn',
      channel: CHANNEL,
      attributes: { flow, permission: name, elapsedMs: elapsed },
    });

  const subscribe = (name: MediaPermissionName, status: PermissionStatus) => {
    const onChange = () => {
      if (!pending || status.state !== 'granted' || grantedAt.has(name)) {
        return;
      }

      const elapsed = elapsedMs();
      grantedAt.set(name, elapsed);
      if (stalled) {
        emitGrantedWhilePending(name, elapsed);
      }
    };

    status.addEventListener('change', onChange);
    unsubscribers.push(() => status.removeEventListener('change', onChange));
  };

  // Queried once at watch time; by the threshold they have had the whole window to resolve,
  // so reading them synchronously there keeps the stall report independent of a hung query.
  let cameraPermission: PermissionStatus | null | undefined;
  let microphonePermission: PermissionStatus | null | undefined;

  Promise.all([queryPermission('camera'), queryPermission('microphone')]).then(
    ([camera, microphone]) => {
      cameraPermission = camera;
      microphonePermission = microphone;

      if (!pending) {
        return;
      }
      if (camera) {
        subscribe('camera', camera);
      }
      if (microphone) {
        subscribe('microphone', microphone);
      }
    }
  );

  const onThreshold = () => {
    stalled = true;
    onStalled();

    emitTelemetryEvent({
      event: 'media.gum.pending',
      message: `[GUM] request still pending — ${flow}`,
      level: 'warn',
      channel: CHANNEL,
      attributes: {
        flow,
        elapsedMs: elapsedMs(),
        visibilityState: document.visibilityState,
        hasFocus: document.hasFocus(),
        labelsAtStartup,
        cameraPermission: cameraPermission?.state,
        microphonePermission: microphonePermission?.state,
        userInteracted: userInteracted(),
      },
    });

    grantedAt.forEach((grantElapsed, name) => emitGrantedWhilePending(name, grantElapsed));
  };

  const timer = setTimeout(onThreshold, GUM_PENDING_THRESHOLD_MS);

  const settle = (outcome: string) => {
    pending = false;
    clearTimeout(timer);
    unsubscribers.forEach((unsubscribe) => unsubscribe());

    if (!stalled) {
      return;
    }

    emitTelemetryEvent({
      event: 'media.gum.pending.settled',
      message: `[GUM] late settle — ${outcome}`,
      level: 'info',
      channel: CHANNEL,
      attributes: { flow, outcome, elapsedMs: elapsedMs() },
    });

    if (!abandoned) {
      onRecovered();
    }
  };

  const settled = request.then(
    (value) => {
      settle('resolved');
      return abandoned ? undefined : value;
    },
    (error: unknown) => {
      settle(rejectionOutcome(error));
      if (abandoned) {
        return undefined;
      }
      throw error;
    }
  );

  const abandon = (action: PendingUserMediaAction) => {
    abandoned = true;

    emitTelemetryEvent({
      event: 'media.gum.pending.action',
      message: `[GUM] user action — ${action}`,
      level: 'info',
      channel: CHANNEL,
      attributes: { flow, action, elapsedMs: elapsedMs() },
    });
  };

  return { settled, abandon };
};
